import { useMutation } from '@apollo/client';
import { AnalyticIdentifier, MonthYearPeriod } from '@common/neo.tax';
import { colors, Stack, useModal } from '@ui-components/neo.tax';

import {
  AnalysisType,
  PeriodName,
  ProcessingTicketingDataState,
  ProviderType,
  TicketingProvider,
  UPDATE_FISCAL_YEAR_PROCESSING_TICKETING_DATA_STATE,
  UpdateFiscalYearProcessingTicketingDataStateMutation,
  UpdateFiscalYearProcessingTicketingDataStateMutationVariables,
} from '@neo.tax/shared/graphql';
import { useDisabledProviders, useGitHubAppInstallReturn } from '@neo.tax/shared/hooks';
import { useAnalyticId } from '@neo.tax/shared/state';

import { AnalysisHero } from '../analysis-hero';
import { ConnectProvidersModal } from '../connect-account-modal';
import { CompanyInfoModal } from './CompanyInformationModal';
import {
  Button,
  CheckCircleIcon,
  ClockIcon,
  SkipAutoConnect,
  StyledSpan,
  UploadedFilesStatus,
} from './get-started-hero.styled';
import { useGettingStartedStatus } from './hooks';
import { StartAnalysisModal } from './start-analysis-modal/StartAnalysisModal';

type GetStartedHeroProps = {
  analysisType: AnalysisType;
  fiscalYearId: string;
  periodName: PeriodName;
  period: MonthYearPeriod;
};

export const GetStartedHero = ({
  analysisType,
  fiscalYearId,
  period,
  periodName,
}: GetStartedHeroProps): React.ReactElement => {
  const skipAutoConnectAnalyticId = useAnalyticId(AnalyticIdentifier.RTC_PROJECT_SKIP_AUTO_CONNECT);

  const { isOpen: companyInfoModalOpen, toggle: toggleCompanyInfoModalOpen } = useModal();
  const { isOpen: isStartAnalysisModalOpen, toggle: toggleStartAnalysisModalOpen } = useModal();

  // The GitHub App install is a full-page redirect away and back (?connected_github_app=...). On
  // return, re-open the connect modal straight to the GitHub slide so the user finishes/polls in the
  // same place (PLAT-386), mirroring how the other providers return into the slide-over.
  const {
    closeConnectModal: closeConnectProviderModal,
    installStatus: githubAppInstallReturn,
    isConnectModalOpen: isConnectProviderModalOpen,
    openConnectModal: openConnectProviderModal,
  } = useGitHubAppInstallReturn();

  const [updateProcessingTicketingDataState] = useMutation<
    UpdateFiscalYearProcessingTicketingDataStateMutation,
    UpdateFiscalYearProcessingTicketingDataStateMutationVariables
  >(UPDATE_FISCAL_YEAR_PROCESSING_TICKETING_DATA_STATE);

  const { canStartAnalysis, companyInfoComplete, connectTicketingProviderComplete, uploadedFilesAwaitProcessing } =
    useGettingStartedStatus({ fiscalYearId });
  const { disabledTicketingProviders } = useDisabledProviders({ fiscalYearId });

  const handleClickStartAnalysis = (): void => {
    toggleStartAnalysisModalOpen();
  };

  const handleClickSkip = (): void => {
    updateProcessingTicketingDataState({
      variables: { fiscalYearId, processingTicketingDataState: ProcessingTicketingDataState.Skipped },
    });
  };

  return (
    <>
      <AnalysisHero
        title={
          <>
            <StyledSpan>Ready to Dive into</StyledSpan>
            <StyledSpan>Projects?</StyledSpan>
          </>
        }
        subtitle="Please complete the following items to get started."
      >
        <>
          <Stack direction="row" gap={1}>
            <Button
              variant="white"
              size="xl"
              rounded="regular"
              onClick={openConnectProviderModal}
              completed={connectTicketingProviderComplete}
              startIcon={connectTicketingProviderComplete ? <CheckCircleIcon /> : undefined}
            >
              Connect Project Management
            </Button>
            <Button
              variant="white"
              size="xl"
              rounded="regular"
              onClick={toggleCompanyInfoModalOpen}
              completed={companyInfoComplete}
              startIcon={companyInfoComplete ? <CheckCircleIcon /> : undefined}
            >
              Company Information
            </Button>
            {uploadedFilesAwaitProcessing ? (
              <UploadedFilesStatus>
                <ClockIcon />
                Our team will process your uploaded files
              </UploadedFilesStatus>
            ) : (
              <Button
                variant="white"
                size="xl"
                rounded="regular"
                disabled={!canStartAnalysis}
                onClick={handleClickStartAnalysis}
              >
                Start Analysis
              </Button>
            )}
          </Stack>
          <SkipAutoConnect
            as="button"
            id={skipAutoConnectAnalyticId}
            color={colors.primary[100]}
            onClick={(): void => {
              handleClickSkip();
            }}
          >
            Skip Auto-Connect
          </SkipAutoConnect>
        </>
      </AnalysisHero>
      <ConnectProvidersModal
        isOpen={isConnectProviderModalOpen}
        close={closeConnectProviderModal}
        showProvidersInTypes={new Set([ProviderType.Ticketing])}
        disabledTicketingProviders={disabledTicketingProviders}
        fiscalYearId={fiscalYearId}
        initialProvider={
          githubAppInstallReturn
            ? { provider: TicketingProvider.GithubIssues, providerType: ProviderType.Ticketing }
            : null
        }
      />
      <CompanyInfoModal isOpen={companyInfoModalOpen} onClose={toggleCompanyInfoModalOpen} />
      <StartAnalysisModal
        isOpen={isStartAnalysisModalOpen}
        onClose={toggleStartAnalysisModalOpen}
        analysisType={analysisType}
        fiscalYearId={fiscalYearId}
        periodName={periodName}
        period={period}
      />
    </>
  );
};
